/**
 * Date Formatting Utility
 *
 * Provides locale-aware date and time formatting throughout the application.
 * Uses the browser's locale (navigator.language) to format dates appropriately.
 *
 * Feature #138: Date picker respects local date format
 */

/**
 * Default fallback locale
 */
const DEFAULT_LOCALE = 'en-GB';

/**
 * Get the user's preferred locale from the browser
 * Falls back to 'en-GB' if not available
 * @returns {string} The user's locale (e.g., 'en-US', 'en-GB', 'de-DE')
 */
export const getUserLocale = () => {
  try {
    // Try to get locale from navigator
    if (typeof window !== 'undefined' && window.navigator) {
      const locale = window.navigator.language ||
                     window.navigator.languages?.[0] ||
                     window.navigator.userLanguage;
      if (locale && typeof locale === 'string' && locale.length > 0) {
        return locale;
      }
    }
  } catch (e) {
    // Silently fall back to default
    console.warn('Could not detect user locale, using default:', DEFAULT_LOCALE);
  }
  // Fallback to 'en-GB' as default
  return DEFAULT_LOCALE;
};

/**
 * Format a date for display with the user's locale
 * @param {Date|string} date - The date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';

  // Get locale with guaranteed fallback
  let locale = getUserLocale();
  if (!locale || locale === 'undefined' || typeof locale !== 'string') {
    locale = DEFAULT_LOCALE;
  }

  // Default options if none provided
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  try {
    return dateObj.toLocaleDateString(locale, { ...defaultOptions, ...options });
  } catch (e) {
    // Final fallback if locale is still invalid
    return dateObj.toLocaleDateString(DEFAULT_LOCALE, { ...defaultOptions, ...options });
  }
};

/**
 * Format a date with weekday for appointment display
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string with weekday
 */
export const formatDateWithWeekday = (date) => {
  return formatDate(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a short date (e.g., for date picker buttons)
 * @param {Date|string} date - The date to format
 * @returns {string} Short formatted date string
 */
export const formatShortDate = (date) => {
  return formatDate(date, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date in a compact format (just day and month)
 * @param {Date|string} date - The date to format
 * @returns {string} Compact formatted date string
 */
export const formatCompactDate = (date) => {
  return formatDate(date, {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format time for display
 * @param {Date|string} date - The date/time to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time string
 */
export const formatTime = (date, options = {}) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';

  // Get locale with guaranteed fallback
  let locale = getUserLocale();
  if (!locale || locale === 'undefined' || typeof locale !== 'string') {
    locale = DEFAULT_LOCALE;
  }

  // Default options for time
  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit'
  };

  try {
    return dateObj.toLocaleTimeString(locale, { ...defaultOptions, ...options });
  } catch (e) {
    // Final fallback if locale is still invalid
    return dateObj.toLocaleTimeString(DEFAULT_LOCALE, { ...defaultOptions, ...options });
  }
};

/**
 * Format both date and time for display
 * @param {Date|string} date - The date/time to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';

  // Get locale with guaranteed fallback
  let locale = getUserLocale();
  if (!locale || locale === 'undefined' || typeof locale !== 'string') {
    locale = DEFAULT_LOCALE;
  }

  const options = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  try {
    return dateObj.toLocaleString(locale, options);
  } catch (e) {
    return dateObj.toLocaleString(DEFAULT_LOCALE, options);
  }
};

/**
 * Get the date format pattern for the current locale
 * Returns a human-readable pattern like "DD/MM/YYYY" or "MM/DD/YYYY"
 * @returns {string} Date format pattern
 */
export const getDateFormatPattern = () => {
  // Get locale with guaranteed fallback
  let locale = getUserLocale();
  if (!locale || locale === 'undefined' || typeof locale !== 'string') {
    locale = DEFAULT_LOCALE;
  }

  // Create a sample date to detect the format
  // Using Dec 31, 2000 because day (31) and month (12) are different
  const sampleDate = new Date(2000, 11, 31); // Dec 31, 2000

  try {
    const formatted = sampleDate.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    // Detect the format by finding positions of known values
    const parts = formatted.split(/[\/\-\.]/);

    if (parts.length !== 3) {
      // Fallback pattern
      return 'DD/MM/YYYY';
    }

    // Determine the order
    const pattern = parts.map(part => {
      if (part === '31') return 'DD';
      if (part === '12') return 'MM';
      if (part === '2000') return 'YYYY';
      return part;
    });

    // Get the separator used
    const separator = formatted.match(/[\/\-\.]/)?.[0] || '/';

    return pattern.join(separator);
  } catch (e) {
    return 'DD/MM/YYYY';
  }
};

/**
 * Format a date for input fields (ISO format: YYYY-MM-DD)
 * This is used for HTML date inputs and API calls
 * @param {Date|string} date - The date to format
 * @returns {string} ISO date string (YYYY-MM-DD)
 */
export const formatDateForInput = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';

  // Return ISO format for input fields
  return dateObj.toISOString().split('T')[0];
};

/**
 * Check if the user's locale uses MM/DD/YYYY format
 * @returns {boolean} True if locale uses month-first format
 */
export const isMonthFirstLocale = () => {
  const pattern = getDateFormatPattern();
  return pattern.startsWith('MM');
};

/**
 * Format relative date (e.g., "Today", "Tomorrow", "In 3 days")
 * @param {Date|string} date - The date to format
 * @returns {string} Relative date string or formatted date
 */
export const formatRelativeDate = (date) => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = new Date(dateObj);
  targetDate.setHours(0, 0, 0, 0);

  const diffDays = Math.round((targetDate - today) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;

  // For dates further away, use the standard format
  return formatShortDate(dateObj);
};

/**
 * Create an ISO datetime string with timezone offset for API calls.
 * This ensures the server interprets the time correctly regardless of server timezone.
 *
 * Feature #137: Appointment times display in user's timezone
 *
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {string} time - Time string in HH:MM format
 * @returns {string} ISO datetime string with timezone offset (e.g., "2026-01-31T10:00:00-03:00")
 */
export const createLocalISOString = (date, time) => {
  if (!date || !time) return '';

  // Create a Date object from the date and time in local timezone
  const localDate = new Date(`${date}T${time}:00`);

  // Check if date is valid
  if (isNaN(localDate.getTime())) return '';

  // Get the timezone offset in minutes and convert to hours:minutes format
  const offsetMinutes = localDate.getTimezoneOffset();
  const offsetSign = offsetMinutes <= 0 ? '+' : '-';
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60).toString().padStart(2, '0');
  const offsetMins = (Math.abs(offsetMinutes) % 60).toString().padStart(2, '0');
  const offsetString = `${offsetSign}${offsetHours}:${offsetMins}`;

  // Format the date components
  const year = localDate.getFullYear();
  const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
  const day = localDate.getDate().toString().padStart(2, '0');
  const hours = localDate.getHours().toString().padStart(2, '0');
  const minutes = localDate.getMinutes().toString().padStart(2, '0');
  const seconds = '00';

  // Return ISO format with timezone offset
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetString}`;
};

export default {
  getUserLocale,
  formatDate,
  formatDateWithWeekday,
  formatShortDate,
  formatCompactDate,
  formatTime,
  formatDateTime,
  getDateFormatPattern,
  formatDateForInput,
  isMonthFirstLocale,
  formatRelativeDate,
  createLocalISOString
};
